"use client";

import * as React from "react";
import { Modal, Button } from "@heroui/react";
import { DropZone } from "@heroui-pro/react";
import { trpc } from "@/lib/trpc";
import {
  uploadFileDirect,
  ensureAnonymousAuth,
  getMediaTypeForMedia,
} from "@hs/shared";
import { RacDialogTriggerAnchor } from "@/components/RacDialogTriggerAnchor";

const CLOUDBASE_ENV_ID = "his-1g5d6vlz6368064f";

export interface MediaUploadOptions {
  categoryId?: string;
  isSharedToSpace?: boolean;
  isOwner?: boolean;
}

type FileFormatColor = "blue" | "gray" | "green" | "orange" | "purple" | "red";

export interface UploadFilesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** 与原先 GlobalUploadContext.startUpload 一致 */
  uploadOptions?: MediaUploadOptions;
  title?: string;
  /** DropZone 副文案 */
  dropZoneDescription?: React.ReactNode;
  /** 每个文件成功落库后调用（默认同原逻辑会 invalidate tRPC；此回调用于额外刷新） */
  onUploaded?: () => void;
  accept?: string;
  multiple?: boolean;
}

type UploadRowStatus = "uploading" | "complete" | "failed";

interface UploadRow {
  id: string;
  file: File;
  name: string;
  size: number;
  status: UploadRowStatus;
  progress: number;
  errorMessage?: string;
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1) : "";
}

function getFormatColor(ext: string): FileFormatColor {
  const map: Record<string, FileFormatColor> = {
    csv: "green",
    doc: "blue",
    docx: "blue",
    jpeg: "blue",
    jpg: "blue",
    json: "orange",
    mp4: "purple",
    pdf: "red",
    png: "green",
    svg: "green",
    ts: "blue",
    tsx: "blue",
    txt: "gray",
    xlsx: "green",
    zip: "orange",
  };
  return map[ext.toLowerCase()] ?? "gray";
}

type DropItem = { kind: string; getFile?: () => Promise<File> };

let uploadRowIdSeq = 0;

/** 非 HTTPS / 旧环境无 `crypto.randomUUID`，需降级避免运行时崩溃 */
function createUploadRowId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    try {
      return c.randomUUID();
    } catch {
      /* secure context 等限制下可能抛错 */
    }
  }
  uploadRowIdSeq += 1;
  return `u-${Date.now()}-${uploadRowIdSeq}-${Math.random().toString(36).slice(2, 11)}`;
}

export function UploadFilesModal({
  isOpen,
  onOpenChange,
  uploadOptions,
  title = "上传文件",
  dropZoneDescription = "将文件拖放到此处，或点击选择文件；支持多选。",
  onUploaded,
  accept,
  multiple = true,
}: UploadFilesModalProps) {
  const [rows, setRows] = React.useState<UploadRow[]>([]);
  const cancelRef = React.useRef(new Map<string, { cancelled: boolean }>());

  const authorizeUpload = trpc.assets.authorizeUpload.useMutation();
  const createMedia = trpc.assets.create.useMutation();
  const utils = trpc.useUtils();

  const categoryId = uploadOptions?.categoryId;
  const isSharedToSpace = uploadOptions?.isSharedToSpace;
  const isOwner = uploadOptions?.isOwner;

  React.useEffect(() => {
    if (isOpen) {
      setRows([]);
      cancelRef.current.clear();
    }
  }, [isOpen]);

  const updateRow = React.useCallback((id: string, patch: Partial<UploadRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const runOne = React.useCallback(
    async (rowId: string, file: File) => {
      const cancelEntry = { cancelled: false };
      cancelRef.current.set(rowId, cancelEntry);

      try {
        const { objectKey } = await authorizeUpload.mutateAsync({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          fileSize: file.size,
        });
        if (cancelEntry.cancelled) return;

        await ensureAnonymousAuth(CLOUDBASE_ENV_ID);
        const result = await uploadFileDirect(
          { envId: CLOUDBASE_ENV_ID },
          file,
          {
            path: objectKey,
            onProgress: (p) => {
              if (cancelEntry.cancelled) return;
              updateRow(rowId, { progress: p.percent });
            },
          }
        );
        if (cancelEntry.cancelled) return;

        await createMedia.mutateAsync({
          name: file.name,
          fileId: result.fileId,
          fileSize: file.size,
          mimeType: file.type || "application/octet-stream",
          ...(isSharedToSpace ? { isSharedToSpace: true } : {}),
          type: getMediaTypeForMedia(file.type, file.name),
          ...(categoryId != null && categoryId !== "" ? { categoryId } : {}),
          ...(isOwner ? { isOwner: true } : {}),
        });

        if (cancelEntry.cancelled) return;

        updateRow(rowId, { status: "complete", progress: 100 });
        void utils.assets.listMy.invalidate();
        if (categoryId != null && categoryId !== "") {
          void utils.assets.listByCategory.invalidate({ categoryId });
          void utils.categories.getCategoryTree.invalidate();
        }
        onUploaded?.();
      } catch (err) {
        if (cancelEntry.cancelled) return;
        const message = err instanceof Error ? err.message : "上传失败";
        updateRow(rowId, {
          status: "failed",
          progress: 0,
          errorMessage: message,
        });
      } finally {
        cancelRef.current.delete(rowId);
      }
    },
    [
      authorizeUpload,
      createMedia,
      utils.assets.listMy,
      utils.assets.listByCategory,
      utils.categories.getCategoryTree,
      categoryId,
      isSharedToSpace,
      isOwner,
      updateRow,
      onUploaded,
    ]
  );

  const enqueueFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const newRows: UploadRow[] = files.map((f) => ({
        id: createUploadRowId(),
        file: f,
        name: f.name,
        size: f.size,
        status: "uploading" as const,
        progress: 0,
      }));
      setRows((prev) => [...newRows, ...prev]);
      for (const r of newRows) {
        void runOne(r.id, r.file);
      }
    },
    [runOne]
  );

  const handleSelect = React.useCallback(
    (fileList: FileList) => {
      enqueueFiles(Array.from(fileList));
    },
    [enqueueFiles]
  );

  const handleDrop = React.useCallback(
    async (e: { items: DropItem[] }) => {
      const dropped: File[] = [];
      for (const item of e.items) {
        if (item.kind === "file" && item.getFile) {
          dropped.push(await item.getFile());
        }
      }
      enqueueFiles(dropped);
    },
    [enqueueFiles]
  );

  const handleRemove = React.useCallback((id: string) => {
    const entry = cancelRef.current.get(id);
    if (entry) entry.cancelled = true;
    cancelRef.current.delete(id);
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        cancelRef.current.forEach((e) => {
          e.cancelled = true;
        });
        cancelRef.current.clear();
      }
      onOpenChange(next);
    },
    [onOpenChange]
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="max-h-[90vh] w-full max-w-lg flex flex-col p-0">
            <Modal.Header className="shrink-0 border-b border-[var(--border)] px-4 py-3">
              <Modal.Heading className="text-xl font-medium">{title}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
              <DropZone className="w-full min-w-0">
                <DropZone.Area
                  onDrop={handleDrop as React.ComponentProps<typeof DropZone.Area>["onDrop"]}
                >
                  <DropZone.Icon />
                  <DropZone.Label>拖放文件到此处，或点击浏览</DropZone.Label>
                  <DropZone.Description>{dropZoneDescription}</DropZone.Description>
                  <DropZone.Trigger>选择文件</DropZone.Trigger>
                </DropZone.Area>
                <DropZone.Input
                  accept={accept}
                  multiple={multiple}
                  onSelect={handleSelect}
                />

                {rows.length > 0 && (
                  <DropZone.FileList>
                    {rows.map((file) => {
                      const ext = getExtension(file.name).toUpperCase() || "FILE";
                      return (
                        <DropZone.FileItem key={file.id} status={file.status}>
                          <DropZone.FileFormatIcon
                            color={getFormatColor(getExtension(file.name))}
                            format={ext}
                          />
                          <DropZone.FileInfo>
                            <DropZone.FileName>{file.name}</DropZone.FileName>
                            <DropZone.FileMeta>
                              {formatFileSize(file.size)}
                              {file.status === "uploading" && ` · ${file.progress}%`}
                              {file.status === "complete" && (
                                <span className="text-emerald-600"> · 完成</span>
                              )}
                              {file.status === "failed" && file.errorMessage && (
                                <span className="text-red-600"> · {file.errorMessage}</span>
                              )}
                            </DropZone.FileMeta>
                            {file.status === "uploading" && (
                              <DropZone.FileProgress value={file.progress}>
                                <DropZone.FileProgressTrack>
                                  <DropZone.FileProgressFill />
                                </DropZone.FileProgressTrack>
                              </DropZone.FileProgress>
                            )}
                          </DropZone.FileInfo>
                          <DropZone.FileRemoveTrigger
                            aria-label={`移除 ${file.name}`}
                            onPress={() => handleRemove(file.id)}
                          />
                        </DropZone.FileItem>
                      );
                    })}
                  </DropZone.FileList>
                )}
              </DropZone>
            </Modal.Body>
            <Modal.Footer className="shrink-0 border-t border-[var(--border)] px-4 py-3">
              <Button variant="ghost" onPress={() => handleModalOpenChange(false)}>
                关闭
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
